import { apiPlugin, storyblokInit } from "@storyblok/react/rsc";
import { cache } from "react";
import { StoryblokMultilink } from "../../.storyblok/types/storyblok";
import { componentsMap } from "./componentsMap";
import { storyblokApiConfig, storyblokConfig } from "./config";
import { createLogger } from "./logger";
import { checkEnvironmentVariable } from "./utilities";

const logger = createLogger();

if (!process.env.NEXT_PUBLIC_STORYBLOK_CONTENT_API_ACCESS_TOKEN) {
  logger.error("NEXT_PUBLIC_STORYBLOK_CONTENT_API_ACCESS_TOKEN is not set");
  throw new Error("NEXT_PUBLIC_STORYBLOK_CONTENT_API_ACCESS_TOKEN is not set");
}

export const getStoryblokApi = storyblokInit({
  accessToken: process.env.NEXT_PUBLIC_STORYBLOK_CONTENT_API_ACCESS_TOKEN!,
  use: [apiPlugin],
  components: componentsMap,
  apiOptions: {
    region: "eu",
  },
});

// Ensure Storyblok is initialized (call this in layouts before rendering Storyblok components)
// This is a no-op function that ensures getStoryblokApi is called, which registers components
export const ensureStoryblokInit = () => {
  getStoryblokApi();
};

/**
 * Fetches a story from Storyblok by slug.
 * Wrapped with React.cache() for per-request deduplication.
 * When generateMetadata() and page component both call this with the same args,
 * only one network request is made.
 */
export const getStoryblokStory = cache(
  async (slug: string[], language?: string) => {
    checkEnvironmentVariable("NEXT_PUBLIC_STORYBLOK_CONTENT_API_ACCESS_TOKEN");
    checkEnvironmentVariable("NEXT_PUBLIC_BASE_PATH");

    try {
      const storyblokApi = getStoryblokApi();

      const fullSlug = slug.length > 0 ? slug.join("/") : "home";


      const { data } = await storyblokApi.get(
        `cdn/stories/${process.env.NEXT_PUBLIC_BASE_PATH}/${fullSlug}`,
        {
          version: storyblokConfig.version,
          language: language,
          cv: Date.now(),
        },
        {
          cache: "no-store"
        },
      );

      return data?.story ?? null;
    } catch {
      return null;
    }
  },
);

export const linkResolver = (
  link: StoryblokMultilink | string | undefined,
): string => {
  if (!link) return "";

  const basePath = process.env.NEXT_PUBLIC_BASE_PATH!;

  if (typeof link === "string") {
    if (!link.includes("http")) {
      return "/".concat(link).replace(`${basePath}/`, "");
    }
    return link.replace(`${basePath}/`, "");
  }

  if (link.cached_url.includes("http")) {
    return link.cached_url || "";
  }

  if (!basePath) {
    return "/".concat(link.cached_url || link.url || "").replaceAll("//", "/");
  }

  let correctUrl = "/"
    .concat(link.cached_url || link.url || "")
    .replaceAll("//", "/");

  // Remove base path if link is to the homepage
  correctUrl =
    correctUrl === `${basePath}/`
      ? correctUrl.replace(basePath, "")
      : correctUrl.replace(`${basePath}/`, "");

  return correctUrl;
};

export const stringLinkResolver = (link: string) => {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH!;
  if (link.includes("http")) {
    return link;
  }

  if (!basePath) {
    return "/".concat(link).replaceAll("//", "/");
  }

  let correctUrl = "/"
    .concat(link)
    .replaceAll("//", "/")
    .replace(`${basePath}/`, "");

  // Remove base path if link is to the homepage
  if (correctUrl === `${basePath}/`) {
    correctUrl = correctUrl.replace(basePath, "");
  }

  if (correctUrl === "/home") {
    return "/";
  }

  return correctUrl;
};

export const getStoriesByUuids = async (uuids: string[], locale?: string) => {
  const storyblok = getStoryblokApi();
  const { data } = await storyblok.get(
    `cdn/stories/${storyblokConfig.basePath}`,
    {
      ...storyblokApiConfig,
      version: storyblokConfig.version,
      per_page: 25,
      by_uuids: uuids.join(","),
      language: locale,
    },
    {
      cache: "no-store"
    },
  );

  return data?.stories || [];
};

export const getSitemapEntries = async () => {
  const storyblok = getStoryblokApi();
  const links = await storyblok.getAll("cdn/links", {
    ...storyblokApiConfig,
    version: storyblokConfig.version,
    starts_with: `${storyblokConfig.basePath}/`,
  });

  return links;
};
